import { useEffect, useState, useRef } from "react";
import { api, resolveAttachmentUrl } from "@/lib/api";
import { PdfViewerModal } from "@/components/PdfViewerModal";
import { GmailMessageModal } from "@/components/GmailMessageModal";
import { EventDetailModal } from "@/components/EventDetailModal";
import { VklassDetailSheet } from "@/components/VklassDetailSheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  Mail,
  GraduationCap,
  BookOpen,
  FileText,
  Newspaper,
  Calendar as CalIcon,
  Paperclip,
} from "lucide-react";
import { toast } from "sonner";

const JARVIS_IMG =
  "https://static.prod-images.emergentagent.com/jobs/47440676-455b-415d-a656-32c4bba91c19/images/e7c5bee9ca26687529352ae6a149086b13e9babdbdee8f65009e93b70ca1b38d.png";

const SOURCE_META = {
  classroom: { label: "Classroom", Icon: GraduationCap, bg: "bg-[#EEF2F7]", fg: "text-[color:var(--jarvis)]" },
  varmdo: { label: "Classroom", Icon: GraduationCap, bg: "bg-[#EEF2F7]", fg: "text-[color:var(--jarvis)]" },
  vklass: { label: "Vklass", Icon: GraduationCap, bg: "bg-[#F0F6E6]", fg: "text-[color:var(--moss)]" },
  gmail: { label: "Gmail", Icon: Mail, bg: "bg-[#FDF2EE]", fg: "text-[#E07A5F]" },
  ical: { label: "Kalender", Icon: BookOpen, bg: "bg-[#F0F6E6]", fg: "text-[color:var(--moss)]" },
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const days = Math.round((d.setHours(0, 0, 0, 0) - new Date(now).setHours(0, 0, 0, 0)) / 86400000);
  if (days === 0) return "Idag";
  if (days === 1) return "Imorgon";
  if (days === -1) return "Igår";
  if (days < 0 && days > -7) return `${Math.abs(days)} dgr sedan`;
  if (days > 0 && days < 14) return `Om ${days} dagar`;
  const m = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
  return `${d.getDate()} ${m[d.getMonth()]}`;
};

export default function WhatsNewPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [summary, setSummary] = useState("");
  const [summarising, setSummarising] = useState(false);
  const [preview, setPreview] = useState(null); // {url, title, mime}
  const [gmailOpen, setGmailOpen] = useState(null); // message_id
  const [eventOpen, setEventOpen] = useState(null); // event_id
  const [vklassOpen, setVklassOpen] = useState(null); // feed-item utan event_id
  const [newCounts, setNewCounts] = useState({ classroom: 0, vklass: 0, gmail: 0, ical: 0 });
  const summaryRef = useRef(null);

  const load = async (f = filter) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/feed/whats-new?filter=${f}&limit=300`);
      setItems(data.items || []);
      // Visa alltid badge baserat på TOTAL (alla källor), oavsett vilken flik
      if (data.new_counts) setNewCounts(data.new_counts);
    } catch (e) {
      // soft fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Hämta först ETT anrop med filter=all så vi får new_counts för alla flikar
    load("all");
    // Nolla "nytt"-räknaren efter en kort delay så badge:n syns en stund
    const t = setTimeout(() => {
      api.post("/feed/mark-seen").catch(() => {});
    }, 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFilterChange = (v) => {
    setFilter(v);
    load(v);
  };

  const askJarvis = async () => {
    setSummarising(true);
    setSummary("");
    try {
      const { data } = await api.post("/feed/summary");
      setSummary(data.reply || "");
      setTimeout(() => summaryRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 100);
    } catch (e) {
      toast.error("Edit kunde inte sammanfatta just nu");
    } finally {
      setSummarising(false);
    }
  };

  return (
    <div className="px-5 pt-3" data-testid="whats-new-page">
      <div className="mb-5">
        <p className="label-eyebrow">Familjens flöde</p>
        <h1 className="font-display text-3xl font-medium mt-0.5">Vad är nytt</h1>
        <p className="text-sm text-[color:var(--text-secondary)] mt-1">
          Allt på ett ställe — Classroom, Gmail och kalender.
        </p>
      </div>

      {/* Edit summary card */}
      <div
        className="rounded-2xl p-5 mb-5 fade-up relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(61,90,128,0.08) 0%, rgba(61,90,128,0.02) 100%)",
          border: "1px solid rgba(61,90,128,0.15)",
        }}
        data-testid="feed-jarvis-card"
        ref={summaryRef}
      >
        <div className="flex items-start gap-4">
          <img
            src={JARVIS_IMG}
            alt="Edit"
            className="w-12 h-12 rounded-full object-cover flex-shrink-0"
            style={{ background: "rgba(61,90,128,0.1)" }}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <Sparkles size={14} className="text-[color:var(--jarvis)]" />
              <span className="text-xs font-semibold text-[color:var(--jarvis)] tracking-wide">
                MORGONBRIEFING
              </span>
            </div>
            {summary ? (
              <p className="text-sm text-[color:var(--text-primary)] whitespace-pre-line leading-relaxed">
                {summary}
              </p>
            ) : (
              <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
                Be Edit lyfta det viktigaste från flödet just nu.
              </p>
            )}
            <button
              onClick={askJarvis}
              disabled={summarising}
              className="mt-3 text-sm font-medium text-[color:var(--jarvis)] hover:underline"
              data-testid="feed-summary-button"
            >
              {summarising ? "Tänker…" : summary ? "Uppdatera briefing" : "Sammanfatta flödet"}
            </button>
          </div>
        </div>
      </div>

      <Tabs value={filter} onValueChange={onFilterChange} className="mb-4">
        <TabsList className="grid grid-cols-5 w-full bg-[color:var(--bg-elevated)] gap-0.5 p-0.5">
          <TabsTrigger value="all" className="text-xs px-1" data-testid="feed-filter-all">Alla</TabsTrigger>
          <TabsTrigger value="vklass" className="text-xs px-1 relative" data-testid="feed-filter-vklass">
            Vklass
            {newCounts.vklass > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[color:var(--moss)] text-white text-[10px] font-semibold flex items-center justify-center"
                data-testid="feed-filter-vklass-badge"
              >
                {newCounts.vklass}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="classroom" className="text-xs px-1 relative" data-testid="feed-filter-classroom">
            Classroom
            {newCounts.classroom > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[color:var(--jarvis)] text-white text-[10px] font-semibold flex items-center justify-center">
                {newCounts.classroom}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="gmail" className="text-xs px-1 relative" data-testid="feed-filter-gmail">
            Mejl
            {newCounts.gmail > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E07A5F] text-white text-[10px] font-semibold flex items-center justify-center">
                {newCounts.gmail}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="ical" className="text-xs px-1" data-testid="feed-filter-ical">Kalender</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <p className="text-xs text-[color:var(--text-tertiary)] text-center mt-8">Laddar…</p>
      ) : items.length === 0 ? (
        <div className="card-soft p-8 text-center" data-testid="feed-empty">
          <p className="text-sm text-[color:var(--text-secondary)]">
            Inget i flödet just nu.
          </p>
          <p className="text-xs text-[color:var(--text-tertiary)] mt-2">
            Anslut ett Google-konto och lägg till iCal-kalendrar i inställningar för att fylla flödet.
          </p>
        </div>
      ) : (
        (() => {
          // På "Alla"-fliken visas ALLT i en platt kronologisk lista (Mail blandas
          // in med Classroom/Kalender efter datum). Andra flikar grupperar per barn/källa.
          const groups = new Map(); // label → items[]
          // Nya poster (is_new) lyfts alltid överst så de syns direkt.
          const newFirst = (arr) =>
            [...arr].sort((a, b) => (b.is_new ? 1 : 0) - (a.is_new ? 1 : 0));
          if (filter === "all") {
            // På "Alla" visa bara saker som faktiskt är "nytt" (Mail, Classroom-stream,
            // coursework). Kalender-events har egen vy + Kalender-flik.
            const actuallyNew = items.filter((it) => it.source !== "ical");
            groups.set("Allt", newFirst(actuallyNew));
          } else {
            for (const it of items) {
              let label = "Övrigt";
              // Plocka ut barn-namnet ur subtitle ("Anton · Kurs", "William · Avsändare", "Anton" osv)
              const sub = (it.subtitle || "").trim();
              const guess = sub.split("·")[0]?.trim();
              if (guess && guess.length > 0 && guess.length < 30) {
                label = guess;
              } else if (it.source === "ical") {
                label = "Familjen";
              } else if (
                it.source === "classroom" ||
                it.source === "varmdo" ||
                it.source === "vklass"
              ) {
                label = "Skola";
              }
              if (!groups.has(label)) groups.set(label, []);
              groups.get(label).push(it);
            }
            for (const [label, arr] of groups) groups.set(label, newFirst(arr));
          }
          // Sortera barn alfabetiskt (Övrigt och Allt sist).
          // Items inom varje grupp behåller backend:s ordning
          // (närmaste datum först, sedan framåt, sedan förflutet).
          const sortedGroupKeys = [...groups.keys()].sort((a, b) => {
            if (a === "Övrigt") return 1;
            if (b === "Övrigt") return -1;
            if (a === "Allt") return -1;
            if (b === "Allt") return 1;
            return a.localeCompare(b, "sv");
          });
          return (
            <div className="space-y-5">
              {sortedGroupKeys.map((label) => (
                <section key={label} data-testid={`feed-group-${label}`}>
                  {label !== "Allt" && (
                    <h2 className="text-xs font-semibold uppercase tracking-wider text-[color:var(--text-secondary)] mb-2 px-1">
                      {label}
                      <span className="text-[color:var(--text-tertiary)] font-normal ml-1.5">
                        · {groups.get(label).length}
                      </span>
                    </h2>
                  )}
                  <ul className="space-y-2 stagger">
                    {groups.get(label).map((it) => {
                      const meta = SOURCE_META[it.source] || SOURCE_META.ical;
                      const Icon =
                        it.type === "test"
                          ? FileText
                          : it.type === "gmail_unread"
                          ? Mail
                          : it.type === "classroom_assignment"
                          ? GraduationCap
                          : meta.Icon;
                      const Body = (
                        <li
                          className={`card-soft p-4 fade-up ${
                            it.is_new
                              ? "ring-1 ring-[#E07A5F]/50 shadow-[0_2px_10px_rgba(224,122,95,0.12)]"
                              : ""
                          }`}
                          data-testid={`feed-item-${it.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-xl ${meta.bg} ${meta.fg} flex items-center justify-center flex-shrink-0`}>
                              <Icon size={16} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                {it.is_new && (
                                  <span
                                    className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-[1px] rounded-full bg-[#E07A5F] text-white"
                                    data-testid={`feed-item-new-badge-${it.id}`}
                                  >
                                    NY
                                  </span>
                                )}
                                <span className={`text-[10px] font-semibold uppercase tracking-wider ${meta.fg}`}>
                                  {meta.label}
                                </span>
                                {it.subtitle && (
                                  <span className="text-[10px] text-[color:var(--text-tertiary)]">
                                    · {it.subtitle}
                                  </span>
                                )}
                                {it.timestamp && (
                                  <span className="text-[10px] flex items-center gap-1 text-[color:var(--text-secondary)] ml-auto">
                                    <CalIcon size={10} /> {fmtWhen(it.timestamp)}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm font-medium leading-snug">{it.title}</p>
                              {it.description && (
                                <p className="text-xs text-[color:var(--text-tertiary)] mt-1.5 line-clamp-2">
                                  {it.description}
                                </p>
                              )}
                              {(it.attachments || []).length > 0 && (
                                <div
                                  className="flex flex-wrap gap-1 mt-2"
                                  data-testid={`feed-attachments-${it.id}`}
                                >
                                  {it.attachments.slice(0, 6).map((att, i) => {
                                    // Prioritera proxy_url (backend-export) över original
                                    // Google Docs/Drive-URL så vi kan visa inline utan
                                    // SAML/inlogg-redirect.
                                    const proxyPath = att.proxy_url || (att.proxied ? att.url : null);
                                    const isProxied = !!proxyPath;
                                    // Förhandsvisning OK om vi har proxy + (PDF mime ELLER bild ELLER fil-ändelse)
                                    const mimeOk =
                                      typeof att.mime === "string" &&
                                      (att.mime.startsWith("application/pdf") ||
                                        att.mime.startsWith("image/"));
                                    const extOk = /\.(pdf|png|jpe?g|gif|webp)(\?|$)/i.test(
                                      att.title || att.url || ""
                                    );
                                    const canPreview = isProxied && (mimeOk || extOk);
                                    // href: proxy_url om vi har, annars original. Token läggs på
                                    // automatiskt av resolveAttachmentUrl för /api/-paths.
                                    const href = resolveAttachmentUrl(proxyPath || att.url);
                                    return (
                                      <a
                                        key={i}
                                        href={href}
                                        target={canPreview ? undefined : "_blank"}
                                        rel="noreferrer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (canPreview) {
                                            e.preventDefault();
                                            setPreview({
                                              url: href,
                                              title: att.title || "Bilaga",
                                              mime: att.mime || "application/pdf",
                                            });
                                          }
                                        }}
                                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-white border border-[color:var(--moss)] text-[color:var(--moss)] hover:bg-[#F0F6E6]"
                                        data-testid={`feed-attachment-${it.id}-${i}`}
                                      >
                                        <Paperclip size={9} />
                                        <span className="truncate max-w-[150px]">{att.title}</span>
                                      </a>
                                    );
                                  })}
                                </div>
                              )}
                              {/* Vklass öppnas alltid i appen (event-modal eller in-app sheet).
                                  Vi länkar aldrig ut härifrån. */}
                              {it.source === "vklass" && (
                                <span className="text-xs text-[color:var(--moss)] mt-1.5 flex items-center gap-1">
                                  Öppna detaljer
                                </span>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                      // Vklass + Classroom-poster: öppna alltid i appen — kalender-modal
                      // om event_id finns, annars en lättviktig in-app sheet med
                      // detaljer + bilagor. Vi länkar ALDRIG ut till externa
                      // Vklass/Classroom-URL:er.
                      // Gmail öppnar mail-modal in-app.
                      const cardClickable =
                        it.source === "gmail" ||
                        it.source === "vklass" ||
                        it.source === "classroom" ||
                        it.source === "varmdo";
                      return cardClickable ? (
                        <div
                          key={it.id}
                          onClick={(e) => {
                            // Öppna länken bara om klicket var på själva kortet — inte på en
                            // inre <a>/<button> (bilage-chip, preview etc.)
                            const tgt = e.target.closest("a, button");
                            if (tgt && tgt !== e.currentTarget) return;
                            if (it.source === "gmail" && it.message_id) {
                              setGmailOpen(it.message_id);
                              return;
                            }
                            if (it.event_id) {
                              setEventOpen(it.event_id);
                              return;
                            }
                            // Vklass/Classroom utan event_id → in-app sheet med
                            // bilagor och full beskrivning.
                            setVklassOpen(it);
                          }}
                          className="block cursor-pointer"
                          data-testid={`feed-item-click-${it.id}`}
                        >
                          {Body}
                        </div>
                      ) : (
                        <div key={it.id}>{Body}</div>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          );
        })()
      )}
      {preview && (
        <PdfViewerModal
          url={preview.url}
          title={preview.title}
          mime={preview.mime}
          onClose={() => setPreview(null)}
        />
      )}
      {gmailOpen && (
        <GmailMessageModal
          messageId={gmailOpen}
          onClose={() => setGmailOpen(null)}
        />
      )}
      {eventOpen && (
        <EventDetailModal
          eventId={eventOpen}
          onClose={() => setEventOpen(null)}
          onChanged={() => load(filter)}
        />
      )}
      {vklassOpen && (
        <VklassDetailSheet
          item={vklassOpen}
          onClose={() => setVklassOpen(null)}
        />
      )}
    </div>
  );
}
